/**
 * Standard meteorological wind-barb geometry, shared by the weather overlay and
 * the own-ship wind vector so the two draw the SAME barb rather than each rolling
 * its own. Speed is read off the feathers near the outer end: half feather = 5 kn,
 * full feather = 10 kn, pennant (filled triangle) = 50 kn, rounded to the nearest
 * 5. The staff points in the direction the wind comes FROM.
 *
 * Pure geometry in nautical miles. The caller scales for zoom with
 * barbScaleForZoom (the barb is drawn in NM, so without scaling it balloons on the
 * way in and shrinks to nothing offshore) and tags the features with whatever
 * layer kinds its own source filters on, so this module needs no map or store.
 */
import type { Feature } from 'geojson';
import { rhumbDestination } from '@/nav';

// Barb geometry base sizes, in nautical miles, at BARB_REF_ZOOM. windBarbFeatures
// scales these by barbScaleForZoom so the barb keeps a sensible on-screen size.
// The whole glyph is scaled by BARB_SIZE, one knob for its overall size. It was
// dialled down after feedback it still read too big at normal sailing zoom
// (fb-mrz99lu9-0). Only the shared factor changes, so every part shrinks together
// and the feather proportions (5/10/50 kn) stay exactly as they were.
const BARB_SIZE = 0.62;
const STAFF_NM = 1.25 * BARB_SIZE;

/**
 * The staff length at scale 1, in NM. Exported so a caller drawing a GRID of
 * barbs can size them to the grid instead of to the zoom (see barbScaleForGrid).
 */
export const STAFF_NM_AT_UNIT_SCALE = STAFF_NM;

/**
 * The scale that makes a barb fit its own cell in a grid.
 *
 * Sizing by zoom alone was wrong in a way that was easy to miss: the barb is
 * drawn in nautical miles and the scale halves per zoom level in, so the barb
 * holds a constant FRACTION OF THE SCREEN at every zoom. Zooming therefore never
 * helped, and with the base sizes as they were that fraction was about half the
 * screen width. Edd's chart came back a lattice of overlapping staffs with the
 * coastline behind it (and it had been reported once before, fb-mrz99lu9-0, and
 * the dial was simply not turned far enough).
 *
 * A grid barb has an obvious right answer: it should fit its cell. Give this the
 * spacing between neighbouring points and the staff comes out at a bit over half
 * of it, so two neighbours cannot reach each other whatever the zoom, the grid
 * density or the screen.
 */
export function barbScaleForGrid(spacingNm: number): number {
  if (!Number.isFinite(spacingNm) || spacingNm <= 0) return 1;
  return (spacingNm * 0.55) / STAFF_NM;
}
const SPACING_NM = 0.26 * BARB_SIZE;
const FULL_NM = 0.46 * BARB_SIZE;
const HALF_NM = 0.24 * BARB_SIZE;
const PENN_BASE_NM = 0.3 * BARB_SIZE;
const PENN_GAP_NM = 0.04 * BARB_SIZE; // small gap between stacked pennants
const PENN_NM = 0.5 * BARB_SIZE;
const FEATHER_DEG = 120; // angle of feathers back from the staff
/** Below this the wind is treated as calm: no staff, no feathers. */
export const CALM_KN = 2.5;

// The zoom at which the barb is drawn at its base NM sizes above. Each zoom level
// in doubles the on-screen pixels-per-NM, so to hold a constant screen size we
// halve the NM per level in (and double it per level out): factor = 2^(ref-zoom).
// Clamped so a heavy zoom either way still leaves a barb that reads cleanly
// without dominating the chart or shrinking away.
const BARB_REF_ZOOM = 12.5;
const BARB_MIN_SCALE = 0.02;
const BARB_MAX_SCALE = 10;

/** NM multiplier for the barb geometry at a given map zoom (clamped). */
export function barbScaleForZoom(zoom: number): number {
  const raw = Math.pow(2, BARB_REF_ZOOM - zoom);
  return Math.min(BARB_MAX_SCALE, Math.max(BARB_MIN_SCALE, raw));
}

/** The layer kinds the caller wants stamped on the barb features, so the staff/
 *  feather lines and the pennant fills each land on the right layer. */
export interface BarbKinds {
  /** The staff and the feather ticks (drawn as lines). */
  line: string;
  /** The pennant triangles (drawn as a fill). */
  pennant: string;
}

/**
 * The staff, feathers, and pennants for a single wind reading at `station`. Empty
 * when calm (under CALM_KN): the caller decides how to mark calm — the weather
 * overlay draws an open ring, the boat's wind vector simply draws nothing. Does
 * NOT emit a station dot; the caller adds one if it wants.
 */
export function windBarbFeatures(
  station: [number, number],
  dirFromDeg: number,
  speedKn: number,
  scale: number,
  kinds: BarbKinds,
): Feature[] {
  const features: Feature[] = [];
  if (speedKn < CALM_KN) return features;

  const staffNm = STAFF_NM * scale;
  const spacingNm = SPACING_NM * scale;
  const fullNm = FULL_NM * scale;
  const halfNm = HALF_NM * scale;
  const pennBaseNm = PENN_BASE_NM * scale;
  const pennGapNm = PENN_GAP_NM * scale;
  const pennNm = PENN_NM * scale;

  const dir = dirFromDeg;
  const tip = rhumbDestination(station, dir, staffNm);
  features.push({
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: [station, tip] },
    properties: { kind: kinds.line },
  });

  let units = Math.round(speedKn / 5) * 5;
  let pennants = Math.floor(units / 50);
  units -= pennants * 50;
  let fulls = Math.floor(units / 10);
  units -= fulls * 10;
  const halfs = units >= 5 ? 1 : 0;

  let along = staffNm; // distance from the station of the current element

  while (pennants-- > 0) {
    const b1 = rhumbDestination(station, dir, along);
    const b2 = rhumbDestination(station, dir, along - pennBaseNm);
    const apex = rhumbDestination(b1, dir + FEATHER_DEG, pennNm);
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [[b1, apex, b2, b1]] },
      properties: { kind: kinds.pennant },
    });
    along -= pennBaseNm + pennGapNm;
  }
  while (fulls-- > 0) {
    const p = rhumbDestination(station, dir, along);
    const f = rhumbDestination(p, dir + FEATHER_DEG, fullNm);
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [p, f] },
      properties: { kind: kinds.line },
    });
    along -= spacingNm;
  }
  if (halfs > 0) {
    // A lone half barb sits one step in from the tip, by convention.
    if (along === staffNm) along -= spacingNm;
    const p = rhumbDestination(station, dir, along);
    const f = rhumbDestination(p, dir + FEATHER_DEG, halfNm);
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [p, f] },
      properties: { kind: kinds.line },
    });
  }
  return features;
}
